import React from "react";

import { stageMapModel } from "./slotStage";
import type { SlotStage } from "./types";

/** Per-slot "map" row: horizontal stepper of the declared steps, time-in-stage, source badge, T1/T2 chips,
 * UNDECLARED warning and an expandable step list. Renders nothing when the backend serves no `stage`. */
export function SlotStageMap({
  stage,
  slotKey,
  nowMs,
}: {
  stage: SlotStage | null | undefined;
  slotKey: string;
  nowMs?: number;
}) {
  const [open, setOpen] = React.useState(false);
  const model = stageMapModel(stage, nowMs ?? Date.now());
  if (model.kind === "absent") return null;
  const testId = (name: string) => `slot-stage-${name}-${slotKey}`;
  return (
    <div className="slot-stage-map" data-testid={testId("map")}>
      <div className="slot-stage-row">
        {model.cells.length > 0 && (
          <ol className="slot-stage-stepper" data-testid={testId("stepper")}>
            {model.cells.map((c) => (
              <li
                key={c.key}
                className={`slot-stage-cell ${c.state}`}
                data-state={c.state}
                title={c.title}
                aria-current={c.state === "current" ? "step" : undefined}
              >
                {c.label}
              </li>
            ))}
          </ol>
        )}
        <span className="slot-stage-current mono" data-testid={testId("current")}>
          {model.currentLabel}
        </span>
        <span className="slot-stage-time mono" data-testid={testId("time")} title="Time in this stage">
          {model.timeInStage}
        </span>
        {model.source && (
          <span className={`slot-stage-source ${model.source}`} data-testid={testId("source")}>
            {model.source}
          </span>
        )}
        {model.chips.map((chip) => (
          <span
            key={chip.tier}
            className={`slot-stage-chip ${chip.tone}`}
            data-testid={testId(`chip-${chip.tier.toLowerCase()}`)}
            title={chip.title}
          >
            {chip.label}
          </span>
        ))}
        {model.undeclared && (
          <span
            className="slot-stage-undeclared"
            data-testid={testId("undeclared")}
            title="The task is over 10 minutes old and the worker has not declared a plan"
          >
            UNDECLARED
          </span>
        )}
        {model.expandable && (
          <button
            type="button"
            className="btn btn-sm slot-stage-toggle"
            data-testid={testId("toggle")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide steps" : "Steps"}
          </button>
        )}
      </div>
      {open && model.expandable && (
        <ul className="slot-stage-steps" data-testid={testId("steps")}>
          {(stage?.steps ?? []).map((st) => (
            <li key={st.n} data-state={st.status}>
              <span className="mono">{st.n}.</span> <span className="mono">{st.type}</span>
              {st.summary ? <span className="slot-stage-step-summary"> — {st.summary}</span> : null}
              <span className="slot-stage-step-status"> ({st.status})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
